import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { DropdownMenu, TableList, TableListActions, Typography } from 'seagull';
import { Copy, Edit, Trash } from 'seagull/icons';
import { VariableInlineRenderer } from '~/components/AgreementStudioPage/TemplateVariables/VariableInlineRenderer/VariableInlineRenderer';
import { recurrencePeriodsTextsPath } from '~/constants';
import { useLocaleKeys } from '~/i18n';
import { serviceTemplateModel } from '~/models';
import { Accounting } from '~/types/api.model';
import { billingTriggerLabel, ColumnId, occurrenceLabel, occurrenceTextsPath, triggerLabelsTextsPath } from './CommonServicesPage.utils';
import styles from './RowItem.module.scss';
import { ServiceRowName } from './ServiceRowName';
import { ServiceRowPrice } from './ServiceRowPrice/ServiceRowPrice';
import { formatPriceDisplay } from './ServiceRowPrice/ServiceRowPrice.utils';
export function RowItem(param) {
    let { service: initialService, qboItemsById, xeroItemsById, serviceNameValidation, onInlineEdit: onInlineEditProp, onEdit, onDuplicate, onDelete, onInputFocus, onInputBlur, columns, isHeightFluid, isMobileDevice, bundlesMode } = param;
    const [service, setService] = useState(initialService);
    const { t: servicesPageTexts } = useLocaleKeys('pages.servicesPage');
    const { t: occurrenceTexts } = useLocaleKeys(occurrenceTextsPath);
    const { t: triggerLabelsTexts } = useLocaleKeys(triggerLabelsTextsPath);
    const { t: recurrencePeriodsTexts } = useLocaleKeys(recurrencePeriodsTextsPath);
    const qboIntegratedId = serviceTemplateModel.integratedId({
        serviceTemplate: service,
        accounting: Accounting.QBO
    });
    const xeroIntegratedId = serviceTemplateModel.integratedId({
        serviceTemplate: service,
        accounting: Accounting.Xero
    });
    useEffect(()=>{
        setService(initialService);
    }, [
        initialService
    ]);
    const onInlineEdit = useCallback((edit)=>{
        const newService = _object_spread({}, service, edit);
        onInlineEditProp(newService);
        setService(newService);
    }, [
        service,
        onInlineEditProp
    ]);
    const rateDisplay = useMemo(()=>formatPriceDisplay(service.cost), [
        service.cost
    ]);
    function nameColumn() {
        var _qboItemsById_qboIntegratedId, _xeroItemsById_xeroIntegratedId;
        return /*#__PURE__*/ _jsx(ServiceRowName, {
            onInputFocus: onInputFocus,
            onInputBlur: onInputBlur,
            service: service,
            serviceNameValidation: serviceNameValidation,
            onInlineEdit: onInlineEdit,
            qboServiceDisplay: qboIntegratedId && ((_qboItemsById_qboIntegratedId = qboItemsById[qboIntegratedId]) === null || _qboItemsById_qboIntegratedId === void 0 ? void 0 : _qboItemsById_qboIntegratedId.name),
            xeroServiceDisplay: xeroIntegratedId && ((_xeroItemsById_xeroIntegratedId = xeroItemsById[xeroIntegratedId]) === null || _xeroItemsById_xeroIntegratedId === void 0 ? void 0 : _xeroItemsById_xeroIntegratedId.name),
            readOnly: isMobileDevice
        });
    }
    function occurenceColumn() {
        return /*#__PURE__*/ _jsx(Typography, {
            skin: "secondary",
            children: occurrenceLabel(service.billing, occurrenceTexts, recurrencePeriodsTexts)
        });
    }
    function billingColumn() {
        return /*#__PURE__*/ _jsx(Typography, {
            ellipsis: true,
            skin: "secondary",
            children: billingTriggerLabel(service.billing, triggerLabelsTexts)
        });
    }
    function includedServicesColumn() {
        var _service_bundle;
        return /*#__PURE__*/ _jsx(Typography, {
            skin: "secondary",
            testId: "service-library-row-included-services",
            children: /*#__PURE__*/ _jsx("ul", {
                className: cn(styles.includedServices, {
                    [styles.mobile]: isMobileDevice
                }),
                children: (_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services.map((param, index)=>{
                    let { name } = param;
                    return /*#__PURE__*/ _jsx("li", {
                        children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                            text: name,
                            mode: "text"
                        })
                    }, "".concat(service.id, "-").concat(index));
                })
            })
        });
    }
    function rateColumn() {
        return /*#__PURE__*/ _jsx(ServiceRowPrice, {
            service: service,
            onInlineEdit: onInlineEdit,
            onInputFocus: onInputFocus,
            onInputBlur: onInputBlur,
            readOnly: isMobileDevice
        });
    }
    function getColumnItems() {
        const columnsMap = {
            [ColumnId.Name]: nameColumn,
            [ColumnId.Rate]: rateColumn,
            [ColumnId.BillingOccurance]: occurenceColumn,
            [ColumnId.BillingTrigger]: billingColumn,
            [ColumnId.IncludedServices]: includedServicesColumn
        };
        return compact(columns.map((column)=>({
                value: columnsMap[column]()
            })));
    }
    return /*#__PURE__*/ _jsx(TableList.Row, {
        testId: "service-library-row",
        rowId: service.id,
        size: isHeightFluid ? 'fluid' : 'medium',
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            buttonText: servicesPageTexts.edit(),
            buttonIcon: /*#__PURE__*/ _jsx(Edit, {}),
            testId: "service-library-row-actions",
            onClickButton: onEdit,
            mobile: {
                title: service.name,
                subtitle: bundlesMode ? undefined : rateDisplay
            },
            children: [
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    bi: {
                        name: 'service_library_row_duplicate'
                    },
                    iconStart: /*#__PURE__*/ _jsx(Copy, {}),
                    testId: "service-library-row-duplicate",
                    onClick: onDuplicate,
                    children: servicesPageTexts.duplicate()
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    bi: {
                        name: 'service_library_row_delete'
                    },
                    skin: "error",
                    iconStart: /*#__PURE__*/ _jsx(Trash, {
                        size: 20
                    }),
                    testId: "service-library-row-delete",
                    onClick: onDelete,
                    children: servicesPageTexts.delete()
                })
            ]
        }),
        items: getColumnItems()
    });
}
